import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import type { ILandingBankLink, ILandingLinkCreated } from '@/entities/landing.entity';
import CopyableUrlRow from '@/pages/landings/components/copyable-url-row';
import { cn } from '@/lib/utils';
import { ChevronDownIcon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

interface CreatedLinkDialogProps {
	link: ILandingLinkCreated | null;
	onClose: () => void;
}

export default function CreatedLinkDialog({ link, onClose }: CreatedLinkDialogProps) {
	const [relatedExpanded, setRelatedExpanded] = useState(false);

	if (!link) return null;

	const hasBankLinks = link.bankLinks && link.bankLinks.length > 0;
	const relatedCount = link.relatedLinks?.length ?? 0;

	function copyText(text: string) {
		navigator.clipboard
			.writeText(text)
			.then(() => toast.success('Скопировано'))
			.catch(() => toast.error('Ошибка копирования'));
	}

	function copyAll() {
		if (!link) return;
		const urls = hasBankLinks
			? link.bankLinks!.map((b: ILandingBankLink) => b.url)
			: [link.url];
		copyText(urls.join('\n'));
	}

	return (
		<Dialog open onOpenChange={open => !open && onClose()}>
			<DialogContent className='min-w-0 overflow-x-hidden sm:max-w-[480px]'>
				<DialogHeader>
					<DialogTitle>{hasBankLinks ? 'Ссылки по банкам' : 'Ссылка создана'}</DialogTitle>
					<DialogDescription>
						Скопируйте ссылку и отправьте трафик. События connect и download появятся в статистике.
					</DialogDescription>
				</DialogHeader>

				<div className='flex min-w-0 flex-col gap-3'>
					{hasBankLinks ? (
						link.bankLinks!.map((bankLink: ILandingBankLink) => (
							<CopyableUrlRow
								key={bankLink.bank}
								label={bankLink.bank}
								url={bankLink.url}
								onCopy={copyText}
							/>
						))
					) : (
						<CopyableUrlRow label='Основная ссылка' url={link.url} onCopy={copyText} />
					)}

					{relatedCount > 0 ? (
						<div className='rounded-md border border-border'>
							<button
								type='button'
								className='flex min-w-0 w-full items-center justify-between gap-2 px-3 py-2 text-sm font-medium'
								onClick={() => setRelatedExpanded(v => !v)}
							>
								<span className='truncate'>Связанные ссылки воронки ({relatedCount})</span>
								<ChevronDownIcon
									size={16}
									className={cn('text-muted-foreground transition-transform', relatedExpanded && 'rotate-180')}
								/>
							</button>
							{relatedExpanded ? (
								<div className='border-t border-border px-3 py-2'>
									<p className='text-xs text-muted-foreground break-all'>{link.relatedLinks!.join(', ')}</p>
								</div>
							) : null}
						</div>
					) : null}
				</div>

				<DialogFooter className='flex-col sm:flex-col'>
					<Button type='button' className='w-full' onClick={copyAll}>
						{hasBankLinks && link.bankLinks!.length > 1 ? 'Скопировать все' : 'Скопировать'}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
